import React, { useMemo } from 'react';
import {
  CategoryConfigurationDefinitionResponse,
  EntityListResponse,
  SkuType,
} from '@ydistri/api-sdk';
import ConfigurationRowTitle from '../ConfigurationRowTitle';
import { isSectionTypeSource, isSectionTypeTarget } from '../../configurationUtils';
import {
  ConfigurationFieldsWrapperCentered,
  ConfigurationFieldsWrapperTarget,
  ConfigurationRowWrapper,
} from '../ConfigurationRow';
import ConfigurationEntityListField from './ConfigurationEntityListField';
import useCategoryId from '../../../../../hooks/useCategoryId';
import { EntityListInfo } from '../../../../ProjectAdministration/EntityLists/entityListsTypes';
import { isRootCategory } from '../../../../../lib/categories/categoriesLib';
import { useEntityListExclusion } from './useEntityListExclusion';
import {
  entityListScopeExcludedConfigurationPairs,
  getConfiguredEntityListVersion,
  isScopeConfigurationWithExcludedVariant,
} from './entityListLib';
import { useCategoryConfiguration } from '../../../../../hooks/useCategoryConfiguration';

interface ConfigurationEntityListRowProps {
  definition: CategoryConfigurationDefinitionResponse;
  entityList: EntityListResponse;
  forcedTitle?: string;
  forcedCategoryId?: number;
  fullyFeatured?: boolean;
  disabled?: boolean;
}

const ConfigurationEntityListRow: React.FC<ConfigurationEntityListRowProps> = ({
  definition,
  entityList,
  forcedTitle,
  forcedCategoryId,
  fullyFeatured = true,
  disabled = false,
}) => {
  const selectedCategoryId = useCategoryId();
  const categoryId = forcedCategoryId ?? selectedCategoryId;
  const { rootCategoryConfiguration, categoryConfigurationData } =
    useCategoryConfiguration(categoryId);

  const entityListInfo: EntityListInfo = useMemo(
    () => ({
      entityList,
      categoryId,
      configuredVersion: getConfiguredEntityListVersion(
        definition.fieldType,
        rootCategoryConfiguration,
        categoryConfigurationData,
        entityList.entityListId,
      ),
    }),
    [
      entityList,
      categoryId,
      definition.fieldType,
      rootCategoryConfiguration,
      categoryConfigurationData,
    ],
  );

  const nonRootUIDisabled = disabled || !isRootCategory(categoryId);

  const title = (forcedTitle ? forcedTitle + ' - ' : '') + (entityList.name ?? '');

  const { isExcluded } = useEntityListExclusion(
    isScopeConfigurationWithExcludedVariant(definition.fieldType)
      ? entityListScopeExcludedConfigurationPairs[definition.fieldType]
      : undefined,
    entityList.entityListId,
  );

  return (
    <ConfigurationRowWrapper $skuListExcluded={isExcluded}>
      <ConfigurationRowTitle
        definition={definition}
        forcedTitle={title}
        entityListInfo={entityListInfo}
        fullyFeatured={fullyFeatured}
        disabled={nonRootUIDisabled}
      />
      {isSectionTypeSource(definition.sectionType) && (
        <ConfigurationFieldsWrapperCentered>
          <ConfigurationEntityListField
            skuTypeColumn={SkuType.DeadStock}
            fieldType={definition.fieldType}
            dataType={definition.dataType}
            disabled={disabled}
            entityList={entityList}
            forcedCategoryId={forcedCategoryId}
          />
          <ConfigurationEntityListField
            skuTypeColumn={SkuType.SlowMover}
            fieldType={definition.fieldType}
            dataType={definition.dataType}
            disabled={disabled}
            entityList={entityList}
            forcedCategoryId={forcedCategoryId}
          />
          <ConfigurationEntityListField
            skuTypeColumn={SkuType.FastMover}
            fieldType={definition.fieldType}
            dataType={definition.dataType}
            disabled={disabled}
            entityList={entityList}
            forcedCategoryId={forcedCategoryId}
          />
        </ConfigurationFieldsWrapperCentered>
      )}
      {isSectionTypeTarget(definition.sectionType) && (
        <ConfigurationFieldsWrapperTarget>
          <ConfigurationEntityListField
            skuTypeColumn="Target"
            fieldType={definition.fieldType}
            dataType={definition.dataType}
            disabled={disabled}
            entityList={entityList}
            forcedCategoryId={forcedCategoryId}
          />
        </ConfigurationFieldsWrapperTarget>
      )}
    </ConfigurationRowWrapper>
  );
};

export default ConfigurationEntityListRow;
